import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, RefreshCw, AlertCircle, CheckCircle, Clock,
  TrendingUp, TrendingDown, DollarSign, Percent, Plus,
  Edit2, Archive, Play, Eye, ChevronDown, ChevronUp,
  AlertTriangle, History, X, ArrowRightLeft, Zap
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useLanguage } from '../../context/LanguageContext';
import { API_URL } from '../../utils/apiConfig';

const STATUS_COLORS = {
  draft: 'bg-slate-700 text-slate-300',
  scheduled: 'bg-amber-900/50 text-amber-400',
  active: 'bg-emerald-900/50 text-emerald-400',
  superseded: 'bg-slate-600 text-slate-400',
  archived: 'bg-red-900/30 text-red-400'
};

export default function AdminFXView() {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentFX, setCurrentFX] = useState(null);
  const [rates, setRates] = useState([]);
  const [revenueAnalysis, setRevenueAnalysis] = useState(null);
  const [activeTab, setActiveTab] = useState('current');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSpreadModal, setShowSpreadModal] = useState(false);
  const [showConverterModal, setShowConverterModal] = useState(false);
  const [expandedRate, setExpandedRate] = useState(null);

  const getToken = () => localStorage.getItem('pecash_token');
  const isAdmin = localStorage.getItem('pecash_admin') === 'true';

  const fetchFXData = useCallback(async () => {
    const token = getToken();
    
    if (!token || !isAdmin) {
      navigate('/admin');
      return;
    }

    try {
      setLoading(true);
      
      // Fetch current FX
      const currentRes = await fetch(`${API_URL}/api/admin/fx/current`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!currentRes.ok) {
        if (currentRes.status === 401 || currentRes.status === 403) {
          throw new Error('Session expired');
        }
        throw new Error('Failed to fetch FX data');
      }
      
      const currentData = await currentRes.json();
      setCurrentFX(currentData);

      // Fetch rates
      const ratesRes = await fetch(`${API_URL}/api/admin/fx/rates?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (ratesRes.ok) {
        const ratesData = await ratesRes.json();
        setRates(ratesData.rates || []);
      }

      // Fetch revenue analysis
      const revenueRes = await fetch(`${API_URL}/api/admin/fx/revenue-analysis?days=30`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (revenueRes.ok) {
        const revenueData = await revenueRes.json();
        setRevenueAnalysis(revenueData);
      }

      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
      if (err.message === 'Session expired') {
        localStorage.removeItem('pecash_token');
        localStorage.removeItem('pecash_admin');
        setTimeout(() => navigate('/admin'), 2000);
      }
    }
  }, [navigate, isAdmin]);

  useEffect(() => {
    fetchFXData();
  }, [fetchFXData]);

  const handleActivateRate = async (rateId) => {
    const token = getToken();
    
    try {
      const res = await fetch(`${API_URL}/api/admin/fx/rates/${rateId}/activate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed to activate rate');
      }
      
      await fetchFXData();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleArchiveRate = async (rateId) => {
    const token = getToken();
    const reason = prompt('Enter reason for archiving:');
    if (!reason) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/fx/rates/${rateId}/archive?reason=${encodeURIComponent(reason)}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed to archive rate');
      }
      
      await fetchFXData();
    } catch (err) {
      setError(err.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-purple-400 animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading FX data...</p>
        </div>
      </div>
    );
  }

  if (error && error === 'Session expired') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center bg-slate-800 rounded-2xl p-8 border border-red-800">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Session Expired</h2>
          <p className="text-red-400 mb-4">{error}</p>
          <p className="text-slate-500 text-sm">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin/dashboard')}
              variant="ghost"
              className="text-slate-400 hover:text-white"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white">Exchange Rate Management</h1>
              <p className="text-sm text-slate-400">USD → DOP Rate Configuration</p>
            </div>
          </div>
          
          <div className="flex gap-3">
            <Button
              onClick={() => setShowConverterModal(true)}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
              data-testid="open-converter"
            >
              <ArrowRightLeft className="w-4 h-4 mr-2" />
              Converter
            </Button>
            <Button
              onClick={() => setShowSpreadModal(true)}
              variant="outline"
              className="border-amber-600 text-amber-400 hover:bg-amber-900/50"
              data-testid="quick-spread-btn"
            >
              <Zap className="w-4 h-4 mr-2" />
              Quick Spread
            </Button>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="create-rate-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Rate
            </Button>
            <Button
              onClick={fetchFXData}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
              data-testid="refresh-fx"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      {/* Error Banner */}
      {error && error !== 'Session expired' && (
        <div className="max-w-7xl mx-auto px-6 pt-4">
          <div className="bg-red-900/20 border border-red-800/50 rounded-xl p-4 flex justify-between items-center">
            <p className="text-red-400 text-sm flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </p>
            <Button variant="ghost" size="sm" onClick={() => setError('')}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Pilot Mode Banner */}
        {currentFX?.pilot_mode && (
          <div className="bg-amber-900/20 border border-amber-800/50 rounded-xl p-4 mb-6">
            <p className="text-amber-300 text-sm flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              PILOT MODE - FX rate changes affect simulated transactions only.
            </p>
          </div>
        )}

        {/* Current Rate Summary */}
        {currentFX && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-slate-800 border border-slate-700 rounded-2xl p-6"
              data-testid="applied-rate-card"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-emerald-900/50 flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-emerald-400" />
                </div>
                <span className="text-slate-400 text-sm">Applied Rate</span>
              </div>
              <p className="text-2xl font-bold text-white">
                {currentFX.current_rate.applied_rate.toFixed(2)}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                1 USD = {currentFX.current_rate.applied_rate.toFixed(2)} DOP
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-slate-800 border border-slate-700 rounded-2xl p-6"
              data-testid="reference-rate-card"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-blue-900/50 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-400" />
                </div>
                <span className="text-slate-400 text-sm">Reference Rate</span>
              </div>
              <p className="text-2xl font-bold text-white">
                {currentFX.current_rate.reference_rate.toFixed(2)}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Market/Interbank rate
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-slate-800 border border-slate-700 rounded-2xl p-6"
              data-testid="spread-card"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-amber-900/50 flex items-center justify-center">
                  <Percent className="w-5 h-5 text-amber-400" />
                </div>
                <span className="text-slate-400 text-sm">Spread</span>
              </div>
              <p className="text-2xl font-bold text-white">
                {currentFX.current_rate.spread.toFixed(2)} DOP
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Per USD (Revenue margin)
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-slate-800 border border-slate-700 rounded-2xl p-6"
              data-testid="source-card"
            >
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  currentFX.current_rate.source === 'database' 
                    ? 'bg-purple-900/50' 
                    : 'bg-slate-700'
                }`}>
                  {currentFX.current_rate.source === 'database' 
                    ? <CheckCircle className="w-5 h-5 text-purple-400" />
                    : <Clock className="w-5 h-5 text-slate-400" />
                  }
                </div>
                <span className="text-slate-400 text-sm">Source</span>
              </div>
              <p className="text-lg font-bold text-white capitalize">
                {currentFX.current_rate.source.replace('_', ' ')}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {currentFX.current_rate.rate_id 
                  ? `Rate: ${currentFX.current_rate.rate_id.slice(0, 8)}...`
                  : 'Using system defaults'
                }
              </p>
            </motion.div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-700 pb-4">
          {[
            { id: 'current', label: 'Current Rate', icon: DollarSign },
            { id: 'rates', label: 'Rate History', icon: History },
            { id: 'revenue', label: 'Revenue Analysis', icon: TrendingUp }
          ].map((tab) => (
            <Button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              variant={activeTab === tab.id ? 'default' : 'ghost'}
              className={activeTab === tab.id 
                ? 'bg-purple-600 text-white' 
                : 'text-slate-400 hover:text-white hover:bg-slate-700'
              }
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </Button>
          ))}
        </div>

        {/* Current Rate Tab */}
        {activeTab === 'current' && currentFX && (
          <div className="space-y-6">
            {/* Rate Details */}
            <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Rate Configuration</h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-slate-900 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Currency Pair</p>
                  <p className="text-white text-lg font-medium">
                    {currentFX.current_rate.currency_from} → {currentFX.current_rate.currency_to}
                  </p>
                </div>
                <div className="bg-slate-900 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Effective From</p>
                  <p className="text-white text-sm">
                    {currentFX.current_rate.effective_from 
                      ? formatDate(currentFX.current_rate.effective_from)
                      : 'System default'
                    }
                  </p>
                </div>
                <div className="bg-slate-900 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Formula</p>
                  <p className="text-slate-300 text-sm font-mono">
                    Applied = Reference - Spread
                  </p>
                </div>
                <div className="bg-slate-900 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Example (${currentFX.spread_revenue_example.transaction_usd})</p>
                  <p className="text-emerald-400 text-lg font-medium">
                    +RD${currentFX.spread_revenue_example.spread_revenue_dop} spread
                  </p>
                </div>
              </div>

              {/* Scheduled Changes */}
              {currentFX.scheduled_changes && currentFX.scheduled_changes.length > 0 && (
                <div>
                  <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-amber-400" />
                    Scheduled Rate Changes
                  </h4>
                  <div className="space-y-2">
                    {currentFX.scheduled_changes.map((scheduled, idx) => (
                      <div 
                        key={idx}
                        className="bg-amber-900/20 border border-amber-800/50 rounded-lg p-3 flex justify-between items-center"
                      >
                        <div>
                          <p className="text-white">
                            Applied: {scheduled.applied_rate.toFixed(2)} DOP (Spread: {scheduled.spread})
                          </p>
                          <p className="text-sm text-amber-400">
                            Effective: {formatDate(scheduled.effective_from)}
                          </p>
                        </div>
                        <span className="px-2 py-1 rounded text-xs bg-amber-900/50 text-amber-400">
                          SCHEDULED
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Rate History Tab */}
        {activeTab === 'rates' && (
          <div className="space-y-4">
            {rates.length === 0 ? (
              <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 text-center">
                <History className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                <p className="text-slate-400 mb-4">No custom FX rates configured.</p>
                <Button 
                  onClick={() => setShowCreateModal(true)}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create First Rate
                </Button>
              </div>
            ) : (
              rates.map((rate) => (
                <motion.div
                  key={rate.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden"
                >
                  <div 
                    className="p-4 cursor-pointer hover:bg-slate-700/50"
                    onClick={() => setExpandedRate(expandedRate === rate.id ? null : rate.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-emerald-900/50 flex items-center justify-center">
                          <DollarSign className="w-5 h-5 text-emerald-400" />
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-white font-medium">
                              {rate.applied_rate.toFixed(2)} DOP
                            </p>
                            <span className="text-slate-400 text-sm">
                              (Ref: {rate.reference_rate.toFixed(2)}, Spread: {rate.spread})
                            </span>
                            <span className={`px-2 py-0.5 rounded text-xs ${STATUS_COLORS[rate.status]}`}>
                              {rate.status}
                            </span>
                          </div>
                          <p className="text-sm text-slate-400">
                            Effective: {formatDate(rate.effective_from)}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {rate.status === 'draft' && (
                          <Button
                            onClick={(e) => { e.stopPropagation(); handleActivateRate(rate.id); }}
                            size="sm"
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid={`activate-rate-${rate.id}`}
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Activate
                          </Button>
                        )}
                        {['draft', 'scheduled'].includes(rate.status) && (
                          <Button
                            onClick={(e) => { e.stopPropagation(); handleArchiveRate(rate.id); }}
                            size="sm"
                            variant="ghost"
                            className="text-red-400 hover:bg-red-900/30"
                          >
                            <Archive className="w-4 h-4" />
                          </Button>
                        )}
                        {expandedRate === rate.id 
                          ? <ChevronUp className="w-5 h-5 text-slate-400" />
                          : <ChevronDown className="w-5 h-5 text-slate-400" />
                        }
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  <AnimatePresence>
                    {expandedRate === rate.id && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="border-t border-slate-700 p-4 bg-slate-900/50"
                      >
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-slate-400">Rate ID</p>
                            <p className="text-white font-mono text-xs">{rate.id}</p>
                          </div>
                          <div>
                            <p className="text-slate-400">Version</p>
                            <p className="text-white">{rate.version}</p>
                          </div>
                          <div>
                            <p className="text-slate-400">Created</p>
                            <p className="text-white">{formatDate(rate.created_at)}</p>
                          </div>
                          <div>
                            <p className="text-slate-400">Effective Until</p>
                            <p className="text-white">
                              {rate.effective_to ? formatDate(rate.effective_to) : 'Indefinite'}
                            </p>
                          </div>
                        </div>
                        
                        {rate.notes && (
                          <div className="mt-4">
                            <p className="text-slate-400 text-sm">Notes</p>
                            <p className="text-white">{rate.notes}</p>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))
            )}
          </div>
        )}

        {/* Revenue Analysis Tab */}
        {activeTab === 'revenue' && revenueAnalysis && (
          <div className="space-y-6">
            <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
              <h3 className="text-lg font-semibold text-white mb-4">
                30-Day FX Spread Revenue Analysis
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <div className="bg-slate-900 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">USD Volume</p>
                  <p className="text-white text-xl font-bold">
                    ${revenueAnalysis.total_usd_volume.toLocaleString()}
                  </p>
                </div>
                <div className="bg-slate-900 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">DOP Sent</p>
                  <p className="text-white text-xl font-bold">
                    RD${revenueAnalysis.total_dop_sent.toLocaleString()}
                  </p>
                </div>
                <div className="bg-slate-900 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Transactions</p>
                  <p className="text-white text-xl font-bold">
                    {revenueAnalysis.transaction_count}
                  </p>
                </div>
                <div className="bg-slate-900 rounded-lg p-4">
                  <p className="text-slate-400 text-sm mb-1">Avg Transaction</p>
                  <p className="text-white text-xl font-bold">
                    ${revenueAnalysis.avg_transaction_usd}
                  </p>
                </div>
                <div className="bg-emerald-900/30 border border-emerald-800/50 rounded-lg p-4">
                  <p className="text-emerald-400 text-sm mb-1">Spread Revenue</p>
                  <p className="text-emerald-400 text-xl font-bold">
                    RD${revenueAnalysis.estimated_spread_revenue_dop.toLocaleString()}
                  </p>
                  <p className="text-xs text-emerald-600 mt-1">
                    @ {revenueAnalysis.current_spread} DOP/USD
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Quick Spread Modal */}
      <AnimatePresence>
        {showSpreadModal && (
          <QuickSpreadModal 
            currentSpread={currentFX?.current_rate?.spread || 2.0}
            onClose={() => setShowSpreadModal(false)} 
            onSuccess={() => { setShowSpreadModal(false); fetchFXData(); }}
          />
        )}
      </AnimatePresence>

      {/* Converter Modal */}
      <AnimatePresence>
        {showConverterModal && (
          <ConverterModal 
            currentRate={currentFX?.current_rate}
            onClose={() => setShowConverterModal(false)} 
          />
        )}
      </AnimatePresence>

      {/* Create Rate Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateRateModal 
            onClose={() => setShowCreateModal(false)} 
            onSuccess={() => { setShowCreateModal(false); fetchFXData(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

function QuickSpreadModal({ currentSpread, onClose, onSuccess }) {
  const [newSpread, setNewSpread] = useState(currentSpread);
  const [reason, setReason] = useState('');
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');

  const handleUpdate = async () => {
    const token = localStorage.getItem('pecash_token');
    setUpdating(true);
    setError('');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/fx/quick-spread-update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ new_spread: newSpread, reason })
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed to update spread');
      }
      
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setUpdating(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-slate-800 border border-slate-700 rounded-2xl p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Quick Spread Update</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-3 mb-4">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="text-slate-400 text-sm block mb-2">
              Current Spread: {currentSpread} DOP/USD
            </label>
            <input
              type="number"
              step="0.1"
              value={newSpread}
              onChange={(e) => setNewSpread(parseFloat(e.target.value) || 0)}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white"
              data-testid="new-spread-input"
            />
          </div>
          
          <div>
            <label className="text-slate-400 text-sm block mb-2">Reason for change</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white"
              placeholder="e.g., Market adjustment"
              data-testid="spread-reason-input"
            />
          </div>

          <div className="pt-4 flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              disabled={updating || !reason}
              className="flex-1 bg-amber-600 hover:bg-amber-700"
              data-testid="update-spread-btn"
            >
              {updating ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              Update Now
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function ConverterModal({ currentRate, onClose }) {
  const [amount, setAmount] = useState(100);
  const [direction, setDirection] = useState('USD_TO_DOP');
  
  const appliedRate = currentRate?.applied_rate || 60;
  
  const result = direction === 'USD_TO_DOP' 
    ? (amount * appliedRate).toFixed(2)
    : (amount / appliedRate).toFixed(2);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-slate-800 border border-slate-700 rounded-2xl p-6 max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Currency Converter</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-slate-400 text-sm block mb-2">Amount</label>
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white"
            />
          </div>
          
          <div>
            <label className="text-slate-400 text-sm block mb-2">Direction</label>
            <select
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white"
            >
              <option value="USD_TO_DOP">USD → DOP</option>
              <option value="DOP_TO_USD">DOP → USD</option>
            </select>
          </div>

          <div className="bg-slate-900 rounded-lg p-4 text-center">
            <p className="text-slate-400 text-sm mb-2">Result (@ {appliedRate} rate)</p>
            <p className="text-emerald-400 text-3xl font-bold">
              {direction === 'USD_TO_DOP' ? `RD$${result}` : `$${result}`}
            </p>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function CreateRateModal({ onClose, onSuccess }) {
  const [referenceRate, setReferenceRate] = useState(62);
  const [spread, setSpread] = useState(2);
  const [effectiveFrom, setEffectiveFrom] = useState(
    new Date().toISOString().slice(0, 16)
  );
  const [notes, setNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const appliedRate = (referenceRate - spread).toFixed(2);

  const handleCreate = async () => {
    const token = localStorage.getItem('pecash_token');
    setCreating(true);
    setError('');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/fx/rates`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          currency_from: 'USD',
          currency_to: 'DOP',
          reference_rate: referenceRate,
          spread: spread,
          effective_from: new Date(effectiveFrom).toISOString(),
          notes: notes || null
        })
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || 'Failed to create rate');
      }
      
      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-slate-800 border border-slate-700 rounded-2xl p-6 max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Create FX Rate</h2>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-3 mb-4">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-slate-400 text-sm block mb-2">Reference Rate (DOP)</label>
              <input
                type="number"
                step="0.01"
                value={referenceRate}
                onChange={(e) => setReferenceRate(parseFloat(e.target.value) || 0)}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white"
                data-testid="reference-rate-input"
              />
            </div>
            <div>
              <label className="text-slate-400 text-sm block mb-2">Spread (DOP/USD)</label>
              <input
                type="number"
                step="0.1"
                value={spread}
                onChange={(e) => setSpread(parseFloat(e.target.value) || 0)}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white"
                data-testid="spread-input"
              />
            </div>
          </div>

          <div className="bg-emerald-900/20 border border-emerald-800/50 rounded-lg p-4 text-center">
            <p className="text-emerald-400 text-sm mb-1">Applied Rate (Customer)</p>
            <p className="text-emerald-400 text-2xl font-bold">{appliedRate} DOP/USD</p>
            <p className="text-xs text-emerald-600 mt-1">
              {referenceRate} - {spread} = {appliedRate}
            </p>
          </div>

          <div>
            <label className="text-slate-400 text-sm block mb-2">Effective From</label>
            <input
              type="datetime-local"
              value={effectiveFrom}
              onChange={(e) => setEffectiveFrom(e.target.value)}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white"
              data-testid="effective-from-input"
            />
          </div>

          <div>
            <label className="text-slate-400 text-sm block mb-2">Notes (optional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-white"
              rows={2}
              placeholder="Optional notes..."
            />
          </div>

          <div className="pt-4 flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 border-slate-600 text-slate-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreate}
              disabled={creating}
              className="flex-1 bg-purple-600 hover:bg-purple-700"
              data-testid="create-rate-submit"
            >
              {creating ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Plus className="w-4 h-4 mr-2" />
              )}
              Create Rate
            </Button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
